import { useEffect, useRef, useState } from 'react'

import ChevronDown from '@Icons/chevron-down.svg?react'
import XClose from '@Icons/competitor-x-close.svg?react'

import { COMPETITOR_ATTRIBUTE_CONFIG, PRODUCT_ATTRIBUTE_CONFIG } from './config'
import type { ConditionRow as ConditionRowType } from './types'
import ValueInput from './value-input'

const CHANNEL_ONLY_ATTRIBUTES = ['my_total_price', 'competitor_total_price']

type Props = {
  row: ConditionRowType
  index: number
  sectionType: 'product' | 'competitor'
  usedAttributes: string[]
  isChannelPackage: boolean
  onUpdate: (id: string, updates: Partial<ConditionRowType>) => void
  onOperatorChange: (id: string, op: 'and' | 'or') => void
  onRemove: (id: string) => void
  onSubmit: () => void
}

export default function ConditionRow({
  row,
  index,
  sectionType,
  usedAttributes,
  isChannelPackage,
  onUpdate,
  onRemove,
  onSubmit,
}: Props) {
  const config =
    sectionType === 'product'
      ? PRODUCT_ATTRIBUTE_CONFIG
      : COMPETITOR_ATTRIBUTE_CONFIG
  const attrConfig = config[row.attribute]
  const [showMenu, setShowMenu] = useState(false)
  const [showOperatorMenu, setShowOperatorMenu] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const operatorMenuRef = useRef<HTMLDivElement>(null)
  const isCompetitor = sectionType === 'competitor'

  useEffect(() => {
    if (!showMenu) return
    const handler = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setShowMenu(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showMenu])

  useEffect(() => {
    if (!showOperatorMenu) return
    const handler = (e: MouseEvent) => {
      if (!operatorMenuRef.current?.contains(e.target as Node))
        setShowOperatorMenu(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showOperatorMenu])

  const currentFilterOperator =
    row.filterOperator ?? attrConfig?.operatorOptions?.[0]?.value ?? ''
  const currentOperatorLabel =
    attrConfig?.operatorOptions?.find((o) => o.value === currentFilterOperator)
      ?.label ??
    attrConfig?.operatorLabel ??
    ''
  const effectiveValueType =
    attrConfig?.valueTypeByOperator?.[currentFilterOperator] ??
    attrConfig?.valueType ??
    'text'

  return (
    <div className="flex items-center gap-2 py-1.5 pl-3">
      {/* Attribute selector */}
      <div className="relative shrink-0" ref={menuRef}>
        <button
          type="button"
          onClick={() => setShowMenu((s) => !s)}
          className={`flex items-center justify-between gap-1.5 w-36 px-3 py-1.5 border rounded text-sm font-medium whitespace-nowrap ${
            isCompetitor
              ? 'bg-green-50 border-green-50 text-green-800 hover:border-green-200'
              : 'bg-blue-50 border-blue-100 text-blue-800 hover:border-blue-200'
          }`}
        >
          {attrConfig?.label ?? row.attribute}
          <ChevronDown className="w-3 h-3 text-gray-400" />
        </button>
        {showMenu && (
          <div className="absolute z-50 top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg min-w-[180px] py-1">
            {Object.entries(config).map(([key, cfg]) => {
              const isCurrentRow = row.attribute === key
              const isUsedByOther =
                !isCurrentRow && usedAttributes.includes(key)
              if (isUsedByOther) return null
              if (!isChannelPackage && CHANNEL_ONLY_ATTRIBUTES.includes(key))
                return null
              return (
                <button
                  key={key}
                  type="button"
                  className={`w-full text-left px-3 py-2 text-sm ${
                    isCurrentRow
                      ? isCompetitor
                        ? 'text-green-700 font-medium bg-green-50'
                        : 'text-blue-700 font-medium bg-blue-50'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                  onClick={() => {
                    const defaultVal =
                      cfg.valueType === 'number-range'
                        ? (['', ''] as [string, string])
                        : ''
                    const defaultFilterOp = cfg.operatorOptions?.[0]?.value
                    onUpdate(row.id, {
                      attribute: key as any,
                      value: defaultVal,
                      filterOperator: defaultFilterOp,
                    })
                    setShowMenu(false)
                  }}
                >
                  {cfg.label}
                </button>
              )
            })}
          </div>
        )}
      </div>

      {/* Operator selector */}
      {attrConfig?.operatorOptions ? (
        <div className="relative shrink-0" ref={operatorMenuRef}>
          <button
            type="button"
            onClick={() => setShowOperatorMenu((s) => !s)}
            className="flex items-center justify-between gap-1 w-32 px-2.5 py-1.5 border border-gray-300 rounded bg-white text-xs text-gray-600 hover:border-gray-400 hover:bg-gray-50 whitespace-nowrap"
          >
            {currentOperatorLabel}
            <ChevronDown className="w-2.5 h-2.5 opacity-50" />
          </button>
          {showOperatorMenu && (
            <div className="absolute z-50 top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg min-w-[160px] py-1">
              {attrConfig.operatorOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`w-full text-left px-3 py-1.5 text-xs ${
                    currentFilterOperator === opt.value
                      ? isCompetitor
                        ? 'text-green-700 font-medium bg-green-50'
                        : 'text-blue-700 font-medium bg-blue-50'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                  onClick={() => {
                    const newValType =
                      attrConfig.valueTypeByOperator?.[opt.value] ??
                      attrConfig.valueType
                    const newVal: string | [string, string] =
                      newValType === 'number-range' ? ['', ''] : ''
                    onUpdate(row.id, {
                      filterOperator: opt.value,
                      value: newVal,
                    })
                    setShowOperatorMenu(false)
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <span className="w-32 px-2.5 py-1.5 text-xs text-gray-400 shrink-0 whitespace-nowrap">
          {attrConfig?.operatorLabel ?? ''}
        </span>
      )}

      {/* Value input or result label */}
      {effectiveValueType === 'none' ? (
        (() => {
          const resultLabel = attrConfig?.operatorOptions?.find(
            (o) => o.value === currentFilterOperator,
          )?.resultLabel
          return resultLabel ? (
            <span className="flex-1 text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded px-2.5 py-1.5">
              {resultLabel}
            </span>
          ) : null
        })()
      ) : (
        <div className="flex-1 min-w-0">
          <ValueInput
            valueType={effectiveValueType}
            value={row.value}
            onChange={(v) => onUpdate(row.id, { value: v })}
            onSubmit={onSubmit}
            filterOperator={row.filterOperator}
            onFilterOperatorChange={(op) =>
              onUpdate(row.id, { filterOperator: op })
            }
          />
        </div>
      )}

      {/* Remove */}
      <button
        type="button"
        onClick={() => onRemove(row.id)}
        className="shrink-0 text-gray-300 hover:text-gray-500 p-0.5 ml-auto"
      >
        <XClose className="w-3.5 h-3.5" />
      </button>
    </div>
  )
}
